import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { getTokensForViewEvent } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Chevron } from "@faire/web--source/slate/icons/foundation/Chevron";
import { Grid } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { isResponsiveProp } from "@faire/web--source/slate/Theme/ResponsiveProp";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React, { useCallback } from "react";
import styled, { css } from "styled-components";
export var GridViewBase = function(props) {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var renderItem = props.renderItem, items = props.items, _props_rows = props.rows, rows = _props_rows === void 0 ? 2 : _props_rows, _props_expandable = props.expandable, expandable = _props_expandable === void 0 ? true : _props_expandable, _props_gap = props.gap, gap = _props_gap === void 0 ? 16 : _props_gap, _props_useArrowToggle = props.useArrowToggle, useArrowToggle = _props_useArrowToggle === void 0 ? false : _props_useArrowToggle, _props_hideCollapse = props.hideCollapse, hideCollapse = _props_hideCollapse === void 0 ? false : _props_hideCollapse, onToggle = props.onToggle, columnTemplate = props.columnTemplate;
    var _React_useContext = React.useContext(Context), layoutElement = _React_useContext.layoutElement, onVisible = _React_useContext.onVisible, requestId = _React_useContext.requestId;
    var _React_useState = _sliced_to_array(React.useState(undefined), 2), isGridRowsVisible = _React_useState[0], setIsGridRowsVisible = _React_useState[1];
    var _props_itemsPerSlide;
    var responsiveItemsPerSlide = isResponsiveProp(props.itemsPerSlide) ? props.itemsPerSlide : {
        mobileAndAbove: (_props_itemsPerSlide = props.itemsPerSlide) !== null && _props_itemsPerSlide !== void 0 ? _props_itemsPerSlide : 2
    };
    var fallbackItemsPerSlide = {
        mobileAndAbove: 2,
        tabletAndAbove: 4,
        desktopAndAbove: 5
    };
    var itemsPerSlide = useResponsiveValue(props.itemsPerSlide ? responsiveItemsPerSlide : fallbackItemsPerSlide);
    var totalVisibleItems = rows * itemsPerSlide;
    var handleComponentVisible = useCallback(function() {
        var start = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 0, end = arguments.length > 1 ? arguments[1] : void 0;
        var _getTokensForViewEvent;
        var endIndex = end;
        var items = (_getTokensForViewEvent = getTokensForViewEvent(layoutElement, strictLocalize)) === null || _getTokensForViewEvent === void 0 ? void 0 : _getTokensForViewEvent.slice(start, endIndex);
        if (layoutElement.on_click_event_name) {
            var _items_join_slice;
            onVisible === null || onVisible === void 0 ? void 0 : onVisible(layoutElement.on_click_event_name, requestId, (_items_join_slice = items === null || items === void 0 ? void 0 : items.join(" ").slice(0, 255)) !== null && _items_join_slice !== void 0 ? _items_join_slice : "");
        }
    }, [
        layoutElement,
        onVisible,
        requestId,
        strictLocalize
    ]);
    var viewRef = useDoActionWhenVisible(function() {
        return handleComponentVisible(0, totalVisibleItems);
    });
    if (items.length < 1) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(GridWrapper, {
        ref: viewRef,
        children: [
            /*#__PURE__*/ _jsx(Grid, {
                columns: itemsPerSlide,
                gap: gap,
                columnTemplate: columnTemplate,
                children: items.slice(0, !expandable ? totalVisibleItems : undefined).map(function(item, index) {
                    return /*#__PURE__*/ _jsx(GridItemWrapper, {
                        $hidden: !isGridRowsVisible && index >= totalVisibleItems,
                        children: renderItem(item, index)
                    }, "".concat(layoutElement.on_click_event_name, "-griditem-").concat(index));
                })
            }),
            expandable && items.length > totalVisibleItems && (!hideCollapse || !isGridRowsVisible) ? /*#__PURE__*/ _jsx(ButtonContentWrapper, {
                children: /*#__PURE__*/ _jsx(Button, {
                    variant: "tertiary",
                    onClick: function() {
                        onToggle === null || onToggle === void 0 ? void 0 : onToggle(isGridRowsVisible);
                        if (isGridRowsVisible === undefined) {
                            setIsGridRowsVisible(true);
                            handleComponentVisible(totalVisibleItems);
                        } else {
                            setIsGridRowsVisible(!isGridRowsVisible);
                        }
                    },
                    children: /*#__PURE__*/ _jsx(ShowMoreShowLessToggle, {
                        useArrowToggle: useArrowToggle,
                        isGridRowsVisible: isGridRowsVisible !== null && isGridRowsVisible !== void 0 ? isGridRowsVisible : false,
                        tileNumber: items.length - totalVisibleItems
                    })
                })
            }) : null
        ]
    });
};
var ShowMoreShowLessToggle = function(param) {
    var useArrowToggle = param.useArrowToggle, isGridRowsVisible = param.isGridRowsVisible, tileNumber = param.tileNumber;
    if (useArrowToggle) {
        var upOrDown = isGridRowsVisible ? "up" : "down";
        return /*#__PURE__*/ _jsx(Chevron, {
            direction: upOrDown,
            "data-test-id": "grid-view-base-chevron-".concat(upOrDown),
            color: Core.text.subdued,
            width: getSpacing("2x"),
            height: getSpacing("2x")
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: isGridRowsVisible ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "TCMjYB",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Show less"
                        }
                    ]
                }),
                /*#__PURE__*/ _jsx(Arrow, {
                    direction: "up",
                    width: "16px",
                    height: "16px"
                })
            ]
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "4cwiCv",
                    defaultMessage: [
                        {
                            type: 6,
                            value: "tileNumber",
                            options: {
                                one: {
                                    value: [
                                        {
                                            type: 0,
                                            value: "Show "
                                        },
                                        {
                                            type: 7
                                        },
                                        {
                                            type: 0,
                                            value: " more"
                                        }
                                    ]
                                },
                                other: {
                                    value: [
                                        {
                                            type: 0,
                                            value: "Show "
                                        },
                                        {
                                            type: 7
                                        },
                                        {
                                            type: 0,
                                            value: " more"
                                        }
                                    ]
                                }
                            },
                            offset: 0,
                            pluralType: "cardinal"
                        }
                    ],
                    values: {
                        tileNumber: tileNumber
                    }
                }),
                /*#__PURE__*/ _jsx(Arrow, {
                    direction: "down",
                    width: "16px",
                    height: "16px"
                })
            ]
        })
    });
};
var ButtonContentWrapper = styled.div.withConfig({
    componentId: "sc-5ed8650c-0"
})([
    "width:100%;text-align:center;padding-top:",
    ";"
], getSpacing("2x"));
var GridItemWrapper = styled.div.withConfig({
    componentId: "sc-5ed8650c-1"
})([
    "",
    ""
], function(param) {
    var $hidden = param.$hidden;
    return $hidden ? css([
        "display:none;"
    ]) : "";
});
var GridWrapper = styled.div.withConfig({
    componentId: "sc-5ed8650c-2"
})([
    "",
    "{padding:",
    ";}"
], responsiveMediaQueries.mobile, getSpacing(0, "2x"));
